import { useMemo } from 'react';
import { useQueries } from '@tanstack/react-query';
import {
  fetchProductAssets,
  productAssetsQueryKey,
  type OptionWithIcon,
  type ProductAssetAudio,
  type ProductAssetVideo,
  type ProductAssetsResult,
} from './useProductAssetsQuery';
import {
  useUserSubscriptionsQuery,
  type UserSubscriptionsResult,
} from './useUserSubscriptionsQuery';
import { PRODUCTS_BY_ID } from '../../constants/products';
import { useAuthUser } from '../../providers/AuthProvider';

export type SelectGroup<TMeta> = {
  group: string;
  items: OptionWithIcon<TMeta>[];
  productId: number;
};

export type ActiveProductAssetsResult = {
  subscriptions: UserSubscriptionsResult | undefined;
  inactiveProductAssets: ProductAssetsResult[];
  videoGroups: SelectGroup<ProductAssetVideo>[];
  audioOptionsByVideoId: Record<number, OptionWithIcon<ProductAssetAudio>[]>;
  rawProductAssets: ProductAssetsResult[];
  isLoading: boolean;
  isFetching: boolean;
  error: Error | null;
};

export function useActiveProductAssetsQuery(
  includeInactiveProductIds: number[] = []
): ActiveProductAssetsResult {
  const user = useAuthUser();
  const subscriptionsQuery = useUserSubscriptionsQuery();
  const activeProductIds = subscriptionsQuery.data?.activeProductIds ?? [];

  const productIdsToFetch = useMemo(() => {
    const ids = new Set<number>(activeProductIds);
    includeInactiveProductIds.forEach(id => ids.add(id));
    return Array.from(ids);
  }, [activeProductIds, includeInactiveProductIds]);

  const queriesEnabled =
    productIdsToFetch.length > 0 &&
    (subscriptionsQuery.isSuccess || includeInactiveProductIds.length > 0);

  const productQueries = useQueries({
    queries: productIdsToFetch.map(productId => ({
      queryKey: productAssetsQueryKey(productId, user?.id),
      queryFn: () => fetchProductAssets(productId),
      enabled: queriesEnabled,
      staleTime: 5 * 60 * 1000,
    })),
  });

  const queriesWithIds = useMemo(
    () =>
      productIdsToFetch.map((productId, index) => ({
        productId,
        query: productQueries[index],
      })),
    [productIdsToFetch, productQueries]
  );

  const videoGroups = useMemo(() => {
    return queriesWithIds
      .map<SelectGroup<ProductAssetVideo> | null>(({ productId, query }) => {
        const data = query?.data;
        if (!data || !activeProductIds.includes(productId)) {
          return null;
        }

        const definition = PRODUCTS_BY_ID[productId];
        const groupLabel = definition?.name ?? data.product.name;

        if (!data.videoOptions.length) {
          return null;
        }

        return {
          group: groupLabel,
          items: data.videoOptions,
          productId,
        };
      })
      .filter((group): group is SelectGroup<ProductAssetVideo> =>
        Boolean(group)
      );
  }, [queriesWithIds, activeProductIds]);

  const audioOptionsByVideoId = useMemo(() => {
    return queriesWithIds.reduce<
      Record<number, OptionWithIcon<ProductAssetAudio>[]>
    >((accumulator, { productId, query }) => {
      const data = query?.data;
      if (!data || !activeProductIds.includes(productId)) {
        return accumulator;
      }

      Object.entries(data.audioOptionsByVideoId).forEach(
        ([videoId, options]) => {
          accumulator[Number(videoId)] = options;
        }
      );

      return accumulator;
    }, {});
  }, [queriesWithIds, activeProductIds]);

  const rawProductAssets = useMemo(() => {
    return queriesWithIds
      .filter(({ productId }) => activeProductIds.includes(productId))
      .map(({ query }) => query?.data)
      .filter((data): data is ProductAssetsResult => Boolean(data));
  }, [queriesWithIds, activeProductIds]);

  const inactiveProductAssets = useMemo(() => {
    return queriesWithIds
      .filter(({ productId }) => !activeProductIds.includes(productId))
      .map(({ query }) => query?.data)
      .filter((asset): asset is ProductAssetsResult => Boolean(asset));
  }, [queriesWithIds, activeProductIds]);

  const queriesLoading = queriesWithIds.some(({ query }) => query?.isLoading);
  const queriesFetching = queriesWithIds.some(({ query }) => query?.isFetching);
  const productErrors = queriesWithIds
    .map(({ query }) => query?.error)
    .find((error): error is Error => Boolean(error));

  return {
    subscriptions: subscriptionsQuery.data,
    inactiveProductAssets,
    videoGroups,
    audioOptionsByVideoId,
    rawProductAssets,
    isLoading:
      subscriptionsQuery.isLoading ||
      (!queriesEnabled && productIdsToFetch.length > 0) ||
      queriesLoading,
    isFetching:
      subscriptionsQuery.isFetching || queriesFetching || queriesLoading,
    error: (subscriptionsQuery.error as Error | null) ?? productErrors ?? null,
  };
}
